import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  additionalSection?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Integration: Faith, Culture, and Modern Life',
    content: [
      '<strong>Objective:</strong> Learn how to integrate faith and culture into modern lifestyles without conflict or guilt.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Navigating conflicts between faith and modern beliefs',
      '• Maintaining cultural integrity in a digital world',
      '• Respecting diversity while honoring your heritage',
      '• Finding balance between tradition and individuality',
      '<br/>',
      '<strong>Discussion:</strong> Modernization often pulls people away from cultural and spiritual roots. True well-being means integrating both worlds – staying open-minded while remaining rooted in your values.'
    ]
  },
  {
    title: 'Navigating Conflicts',
    content: [
      'Navigating conflicts between faith and modern beliefs involves finding harmony between traditional values and contemporary life.',
      '<br/>',
      'This requires flexibility and critical thinking.'
    ]
  },
  {
    title: 'Maintaining Cultural Integrity',
    content: [
      'Maintaining cultural integrity in a digital world means preserving core values while adapting to new technologies and global influences.',
      '<br/>',
      'It fosters resilience in a changing environment.'
    ]
  },
  {
    title: 'Respecting Diversity',
    content: [
      'Respecting diversity while honoring your heritage involves appreciating different perspectives without losing your cultural identity.',
      '<br/>',
      'This builds empathy and global awareness.'
    ]
  },
  {
    title: 'Finding Balance',
    content: [
      'Finding balance between tradition and individuality means integrating personal growth with cultural heritage.',
      '<br/>',
      'It promotes authentic well-being.'
    ]
  },
  {
    title: 'Activity',
    content: [
      'Reflect on one tradition or belief you\'ve questioned. How can you reinterpret it in a way that still honors its spirit but fits your present life?'
    ],
    additionalSection: {
      title: 'Reflection',
      content: [
        'Consider how to integrate faith and culture into your modern lifestyle.'
      ]
    }
  },
  {
    title: 'Key Takeaway',
    content: [
      'Cultural and spiritual maturity is not about choosing between old and new – it\'s about harmonizing both.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You have completed Module 5: Integration: Faith, Culture, and Modern Life! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about integration and balance.',
        'The quiz will help you review key concepts about faith, culture, and modern life.'
      ]
    }
  }
];

const ReligioTraditioCulturalModule5Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('rtcM5');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/religio-traditio-cultural-module-5/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/religio-traditio-cultural-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left religio-traditional-cultural-panel"
        >
          <div className="module-header">
            <h1>Module 5</h1>
            <p className="module-subtitle">Integration: Faith, Culture, and Modern Life</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn how to integrate faith and culture into modern lifestyles without conflict or guilt.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {safeCurrentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/religio-traditio-cultural-module-5/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReligioTraditioCulturalModule5Page;


